import {
  Flex,
  FormLabel,
  IconButton,
  NumberInput,
  NumberInputField,
  Select,
  SelectProps,
} from "@chakra-ui/react";
import { clamp } from "lodash";
import { useEffect, useState } from "react";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";

const MAX_PAGE_SELECT_OPTIONS = 10;

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
  onChange: (currentPage: number, pageSize: number) => void;
};

type PageNumberInputProps = {
  id: string;
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
};
const PageNumberInput = ({
  id,
  currentPage,
  totalPages,
  onChange,
}: PageNumberInputProps) => {
  const [inputValue, setInputValue] = useState<number | null>(currentPage);
  useEffect(() => {
    setInputValue(currentPage);
  }, [currentPage]);

  const sendPageNumber = () => {
    const clampedValue = clamp(inputValue ?? currentPage, 1, totalPages);
    if (clampedValue !== inputValue) setInputValue(clampedValue);
    onChange(clampedValue);
  };

  const handleChange = (_valueAsString: string, valueAsNumber: number) => {
    setInputValue(isNaN(valueAsNumber) ? null : Math.round(valueAsNumber));
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") sendPageNumber();
    else if (e.key === "Escape") setInputValue(currentPage);
  };

  return (
    <NumberInput
      value={inputValue ?? ""}
      min={1}
      max={totalPages}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={sendPageNumber}
      size="md"
      width="4rem"
    >
      <NumberInputField
        id={id}
        fontWeight={700}
        textAlign="center"
        placeholder={currentPage.toString()}
        padding={2}
      />
    </NumberInput>
  );
};

type PageSelectProps = {
  currentPage: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
  onChange: (page: number) => void;
};
const PageSelect = ({
  currentPage,
  totalPages,
  onChange,
  hasPrevious,
  hasNext,
}: PageSelectProps) => (
  <Flex direction="row" alignItems="center" gap={2}>
    <IconButton
      borderRadius="9999px"
      aria-label="Previous"
      variant="outline"
      icon={<FaAngleLeft />}
      onClick={() => onChange(currentPage - 1)}
      isDisabled={!hasPrevious}
    />
    {totalPages <= MAX_PAGE_SELECT_OPTIONS ? (
      <Select
        id="PageSelect"
        fontWeight={700}
        value={currentPage}
        onChange={(e: { target: { value: any } }) =>
          onChange(Number(e.target.value))
        }
      >
        {[...Array(totalPages)].map((e, i) => (
          <option key={i + 1} value={i + 1}>
            {i + 1}
          </option>
        ))}
      </Select>
    ) : (
      <PageNumberInput
        id="PageSelect"
        currentPage={currentPage}
        totalPages={totalPages}
        onChange={onChange}
      />
    )}
    <FormLabel
      htmlFor="PageSelect"
      aria-label={`of ${totalPages}`}
      margin="0"
      flexShrink="0"
    >
      of {totalPages}
    </FormLabel>
    <IconButton
      borderRadius="full"
      aria-label="next"
      variant="outline"
      icon={<FaAngleRight />}
      onClick={() => onChange(currentPage + 1)}
      isDisabled={!hasNext}
    />
  </Flex>
);

type ItemSizeSelectProps = SelectProps & {
  selected: number;
  onChanged: (selected: number) => void;
};
const ItemSizeSelect = ({
  selected,
  onChanged,
  ...props
}: ItemSizeSelectProps) => {
  return (
    <Flex direction="row" alignItems="center" gap={2}>
      <FormLabel htmlFor="ItemSizeSelect" margin="0" flexShrink="0">
        Items per page
      </FormLabel>
      <Select
        id="ItemSizeSelect"
        value={selected}
        onChange={(e: { target: { value: any } }) =>
          onChanged(Number(e.target.value))
        }
        {...props}
      >
        <option value={25}>25</option>
        <option value={50}>50</option>
        <option value={100}>100</option>
        <option value={500}>500</option>
      </Select>
    </Flex>
  );
};

export function Pagination({
  currentPage,
  totalPages,
  pageSize,
  hasNext,
  hasPrevious,
  onChange,
}: PaginationProps) {
  return (
    <Flex gap="4rem">
      <ItemSizeSelect
        selected={pageSize}
        onChanged={(value) => onChange(1, Number(value))}
      />
      <PageSelect
        currentPage={currentPage}
        totalPages={totalPages}
        onChange={(page: number) => onChange(page, pageSize)}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
      />
    </Flex>
  );
}
